import { useState } from "react";
import { toast } from "sonner";
import { PageHero } from "@/components/site/Sections";
import { Field, SelectField, TextArea, FileField } from "@/components/site/FormControls";
import { submitProperty } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

const AUCTION_TYPES = ["Tax Sale", "Sheriff Sale", "Foreclosure", "Tax Foreclosure", "Other", "Not Sure"];
const SERVICES = [
  "Pre-Auction Inspection", "Pre-Auction Support", "Auction Day Support",
  "$2,000 Acquisition Package", "$3,500 Acquisition + Possession", "Financing",
  "Title Coordination", "Rehab Assessment", "Clean-Out", "Other",
];
const OCCUPIED = ["Yes", "No", "Unknown"];
const FINANCING = ["Yes", "No", "Maybe"];

const initial = {
  first_name: "", last_name: "", company: "", phone: "", email: "", investor_location: "",
  property_address: "", city: "", county: "", state: "Pennsylvania", parcel_number: "",
  auction_date: "", auction_type: "", auction_url: "", occupied: "Unknown",
  service_needed: "", need_financing: "No", notes: "",
};

export default function SubmitProperty() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitProperty(form);
      setDone(true);
      toast.success("Property received — our team will review your request.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div>
        <PageHero eyebrow="Submitted" title="Property Received." />
        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-xl px-4 text-center lg:px-8">
            <CheckCircle2 className="mx-auto h-16 w-16 text-gold" />
            <p className="mt-6 text-base leading-relaxed text-slate-600">
              Our team will review your request and contact you regarding service availability and next steps.
            </p>
            <a href="/" className="mt-8 inline-block rounded-full border border-gold/60 px-6 py-3 text-sm font-semibold text-gold hover:bg-gold/10">Back to Home</a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        eyebrow="Request Auction Support"
        title="Submit a Property."
        subtitle="Send us the property and pick a service. A new investor can complete this in about 3–5 minutes."
      />

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <form data-testid="submit-property-form" onSubmit={onSubmit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-10">
            <h3 className="font-mono-x text-xs uppercase tracking-widest text-gold">Your Details</h3>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field label="First Name" name="first_name" value={form.first_name} onChange={set} required half testid="sp-first-name" />
              <Field label="Last Name" name="last_name" value={form.last_name} onChange={set} required half testid="sp-last-name" />
              <Field label="Company" name="company" value={form.company} onChange={set} half testid="sp-company" />
              <Field label="Investor Location" name="investor_location" value={form.investor_location} onChange={set} half testid="sp-location" />
              <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={set} required half testid="sp-phone" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={set} required half testid="sp-email" />
            </div>

            <h3 className="mt-8 font-mono-x text-xs uppercase tracking-widest text-gold">Property</h3>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field label="Property Address" name="property_address" value={form.property_address} onChange={set} required testid="sp-address" />
              <Field label="City" name="city" value={form.city} onChange={set} half testid="sp-city" />
              <Field label="County" name="county" value={form.county} onChange={set} required half testid="sp-county" />
              <Field label="State" name="state" value={form.state} onChange={set} half testid="sp-state" />
              <Field label="Parcel Number" name="parcel_number" value={form.parcel_number} onChange={set} half testid="sp-parcel" />
              <Field label="Auction Date" name="auction_date" type="date" value={form.auction_date} onChange={set} half testid="sp-auction-date" />
              <SelectField label="Auction Type" name="auction_type" value={form.auction_type} onChange={set} options={AUCTION_TYPES} half testid="sp-auction-type" />
              <Field label="Auction URL" name="auction_url" value={form.auction_url} onChange={set} half testid="sp-auction-url" />
              <SelectField label="Property Occupied?" name="occupied" value={form.occupied} onChange={set} options={OCCUPIED} half testid="sp-occupied" />
            </div>

            <h3 className="mt-8 font-mono-x text-xs uppercase tracking-widest text-gold">Service</h3>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <SelectField label="Service Needed" name="service_needed" value={form.service_needed} onChange={set} options={SERVICES} required testid="sp-service" />
              <SelectField label="Need Financing?" name="need_financing" value={form.need_financing} onChange={set} options={FINANCING} half testid="sp-financing" />
              <FileField label="Attach Documents (optional)" testid="sp-files" onChange={() => toast.info("Files noted. A coordinator will request uploads to your secure file.")} />
              <TextArea label="Notes" name="notes" value={form.notes} onChange={set} placeholder="Anything we should know about this property or auction…" testid="sp-notes" />
            </div>

            {(form.need_financing === "Yes" || form.need_financing === "Maybe") && (
              <div className="mt-6 rounded-lg border border-gold/30 bg-gold/5 p-4 text-sm text-slate-600">
                We'll create a financing lead linked to <strong>this same property</strong> — no duplicate records.
                You can add details on the <a href="/request-financing" className="text-gold underline">financing form</a>.
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-property-form-submit-btn"
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 text-sm font-semibold text-[#0B132B] transition-all hover:bg-[#E5C158] disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? "Submitting…" : "Submit Property"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
